import { useState } from 'react';

import { Box, Step, StepLabel, Stepper, Typography, useMediaQuery, useTheme } from '@mui/material';
import dynamic from 'next/dynamic';

import { AgendaModal } from '@/types/generated';
import { Maybe } from '@/types';
import TAGSvgIcon, { IconName } from '@/components/TAGSvgIcon';

import { IAgendaStepperProps } from '../AgendaStepper';

import { getLinkTitleStyles, stepLabelStyles, stepperContainerStyle, stepperStyles } from './TAGStepper.styles';

const StepperModal = dynamic(() => import('../StepperModal/StepperModal'), { ssr: false });

const TAGStepper = ({
	hideStepperDot,
	stepsCollection,
	useNumericStepper,
	isHighlightedSteps,
	areStepsCenteredForMobile,
}: Pick<
	IAgendaStepperProps,
	'stepsCollection' | 'hideStepperDot' | 'useNumericStepper' | 'isHighlightedSteps' | 'areStepsCenteredForMobile'
>) => {
	const theme = useTheme();
	const [open, setOpen] = useState<boolean>(false);
	const [clickedOnStepIndex, setClickedOnStepIndex] = useState<number>(0);
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

	const handleOpen = () => {
		setOpen(true);
	};

	const handleClose = () => {
		setClickedOnStepIndex(0);
		setOpen(false);
	};

	if (!stepsCollection?.items?.length) {
		return null;
	}

	const withStepIcon = stepsCollection.items.some((step) => step && Boolean(step.icon));
	return (
		<>
			<StepperModal
				stepsCollection={stepsCollection}
				clickedOnStepIndex={clickedOnStepIndex}
				open={open}
				handleClose={handleClose}
				handleOpen={handleOpen}
			/>
			<Box sx={stepperContainerStyle(isSmallScreen)}>
				<Stepper
					alternativeLabel={isSmallScreen ? false : true}
					activeStep={stepsCollection.items.length}
					orientation={isSmallScreen ? 'vertical' : 'horizontal'}
					sx={stepperStyles({
						theme,
						isSmallScreen,
						hideStepperDot,
						useNumericStepper,
						withStepIcon,
						areStepsCenteredForMobile,
					})}
					data-testid={'agenda_stepper_stepper'}
				>
					{stepsCollection.items.map((step: Maybe<AgendaModal>, index: number) => {
						if (step?.headlineText) {
							return (
								<Step key={step.headlineText} data-testid={`agenda_stepper_step_index_${index}`}>
									<StepLabel
										sx={stepLabelStyles({
											theme,
											isSmallScreen,
											hideStepperDot,
											useNumericStepper,
											withStepIcon: Boolean(step.icon),
										})}
										StepIconProps={{ completed: false }}
										data-testid={`agenda_stepper_step_label_${index}`}
										{...(step.icon && {
											StepIconComponent: () => (
												<TAGSvgIcon icon={step.icon as IconName} size={24} />
											),
										})}
									>
										<Typography
											data-testid={`agenda_stepper_step_link_${step.headlineText}`}
											onClick={() => {
												setClickedOnStepIndex(index);
												handleOpen();
											}}
											sx={getLinkTitleStyles(theme, isSmallScreen, isHighlightedSteps)}
										>
											{step.headlineText}
										</Typography>
									</StepLabel>
								</Step>
							);
						}
						return null;
					})}
				</Stepper>
			</Box>
		</>
	);
};

export default TAGStepper;
